import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Sidebar = ({ isCollapsed = false, onToggleCollapse, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentRole, setCurrentRole] = useState('student');
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);

  const roleColors = {
    student: 'bg-primary',
    employer: 'bg-success',
    institution: 'bg-secondary',
    admin: 'bg-error'
  };

  const roleLabels = {
    student: 'Student',
    employer: 'Employer',
    institution: 'Institution',
    admin: 'Admin'
  };

  const menuItems = [
    {
      label: 'Dashboard',
      path: '/role-based-dashboard',
      icon: 'LayoutDashboard',
      roles: ['student', 'employer', 'institution', 'admin'],
      badge: true
    },
    {
      label: 'Live Chat',
      path: '/live-chat-communication-hub',
      icon: 'MessageCircle',
      roles: ['student', 'employer', 'institution', 'admin'],
      badge: false
    },
    {
      label: 'Student',
      icon: 'GraduationCap',
      roles: ['student'],
      children: [
        {
          label: 'Profile Management',
          path: '/student-profile-management',
          icon: 'UserCircle',
          roles: ['student']
        },
        {
          label: 'Job Search',
          path: '/job-search-application',
          icon: 'Search',
          roles: ['student']
        }
      ]
    },
    {
      label: 'Employer',
      icon: 'Building2',
      roles: ['employer'],
      children: [
        {
          label: 'Company Profile',
          path: '/employer-registration-profile',
          icon: 'Building2',
          roles: ['employer']
        },
        {
          label: 'Post Job/Internship',
          path: '/job-posting',
          icon: 'PlusCircle',
          roles: ['employer']
        },
        {
          label: 'Candidate Management',
          path: '/candidate-management',
          icon: 'Users',
          roles: ['employer']
        },
        {
          label: 'Interview Scheduling',
          path: '/interview-scheduling-management',
          icon: 'Calendar',
          roles: ['employer']
        },
        {
          label: 'Analytics & Reports',
          path: '/analytics-reporting-dashboard',
          icon: 'BarChart3',
          roles: ['employer']
        },
        {
          label: 'Predictive Analytics',
          path: '/predictive-analytics-custom-reporting',
          icon: 'TrendingUp',
          roles: ['employer']
        },
        {
          label: 'Pricing & Plans',
          path: '/pricing-subscription-plans',
          icon: 'CreditCard',
          roles: ['employer']
        },
        {
          label: 'Checkout',
          path: '/secure-checkout-payment',
          icon: 'ShoppingCart',
          roles: ['employer']
        },
        {
          label: 'Payment Confirmation',
          path: '/payment-confirmation-receipt',
          icon: 'CheckCircle',
          roles: ['employer']
        }
      ]
    },
    {
      label: 'Institution',
      icon: 'School',
      roles: ['institution', 'admin'],
      children: [
        {
          label: 'Institution Portal',
          path: '/institution-management-portal',
          icon: 'Building',
          roles: ['institution', 'admin']
        },
        {
          label: 'Analytics & Reports',
          path: '/analytics-reporting-dashboard',
          icon: 'BarChart3',
          roles: ['institution', 'admin']
        },
        {
          label: 'Predictive Analytics',
          path: '/predictive-analytics-custom-reporting',
          icon: 'TrendingUp',
          roles: ['institution', 'admin']
        }
      ]
    },
    {
      label: 'Admin',
      icon: 'Shield',
      roles: ['admin'],
      children: [
        {
          label: 'Registration Management',
          path: '/registration-management-approval-center',
          icon: 'ClipboardCheck',
          roles: ['admin']
        },
        {
          label: 'Institution Management',
          path: '/institution-management-portal',
          icon: 'Building',
          roles: ['admin']
        },
        {
          label: 'Analytics & Reports',
          path: '/analytics-reporting-dashboard',
          icon: 'BarChart3',
          roles: ['admin']
        },
        {
          label: 'Predictive Analytics',
          path: '/predictive-analytics-custom-reporting',
          icon: 'TrendingUp',
          roles: ['admin']
        },
        {
          label: 'Candidate Overview',
          path: '/candidate-management',
          icon: 'Users',
          roles: ['admin']
        }
      ]
    }
  ];

  const filterMenuByRole = (items) => {
    return items?.filter(item => item?.roles?.includes(currentRole))?.map(item => ({
        ...item,
        children: item?.children ? filterMenuByRole(item?.children) : undefined
      }));
  };

  const filterMenuBySearch = (items) => {
    if (!searchQuery) return items;
    return items?.filter(item => {
      const matchesLabel = item?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      const matchesChildren = item?.children?.some(child => 
        child?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
      return matchesLabel || matchesChildren;
    });
  };

  const filteredMenu = filterMenuBySearch(filterMenuByRole(menuItems));

  const toggleSection = (label) => {
    setExpandedSections(prev => ({
      ...prev,
      [label]: !prev?.[label]
    }));
  };

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      if (isMobile) {
        onToggleCollapse();
      }
    }
  };

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const hasActiveChild = (children) => {
    if (!children) return false;
    return children?.some(child => isActive(child?.path));
  };

  useEffect(() => {
    const activeSection = filteredMenu?.find(item => 
      item?.children && hasActiveChild(item?.children)
    );
    if (activeSection) {
      setExpandedSections(prev => ({
        ...prev,
        [activeSection?.label]: true
      }));
    }
  }, [location?.pathname]);

  useEffect(() => {
    if (searchQuery) {
      const allSections = {};
      filteredMenu?.forEach(item => {
        if (item?.children) {
          allSections[item?.label] = true;
        }
      });
      setExpandedSections(allSections);
    }
  }, [searchQuery]);

  return (
    <aside
      className={`fixed lg:fixed top-0 left-0 h-full bg-card border-r border-border transition-all duration-250 ease-smooth z-100 flex flex-col ${
        isCollapsed ? 'w-20' : 'w-60'
      }`}
    >
      <div className="sidebar-header">
        <div className="flex items-center gap-3">
          <div className="sidebar-logo">
            <Icon name="Briefcase" size={24} color="currentColor" />
          </div>
          {!isCollapsed && (
            <div className="sidebar-logo-text">
              <h2 className="text-lg font-heading font-semibold text-card-foreground">
                JOB Fusion
              </h2>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        {!isCollapsed && (
          <div className="relative w-full">
            <button
              onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
              className="flex items-center justify-between w-full gap-2 px-3 py-2 rounded-md hover:bg-muted transition-smooth"
            >
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${roleColors?.[currentRole]}`} />
                <span className="text-sm font-medium text-card-foreground">
                  {roleLabels?.[currentRole]}
                </span>
              </div>
              <Icon
                name={showRoleSwitcher ? 'ChevronUp' : 'ChevronDown'}
                size={16}
                color="var(--color-muted-foreground)"
              />
            </button>
            {showRoleSwitcher && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 overflow-hidden">
                {Object.keys(roleLabels)?.map((role) => (
                  <button
                    key={role}
                    onClick={() => {
                      setCurrentRole(role);
                      setShowRoleSwitcher(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-smooth ${
                      currentRole === role ? 'bg-primary/10 text-primary' : 'text-card-foreground'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${roleColors?.[role]}`} />
                    <span className="font-medium">{roleLabels?.[role]}</span>
                    {currentRole === role && (
                      <Icon
                        name="Check"
                        size={16}
                        color="var(--color-primary)"
                        className="ml-auto"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-md hover:bg-muted transition-smooth"
          aria-label={isMobile ? 'Close sidebar' : (isCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
        >
          <Icon
            name={isMobile ? 'X' : (isCollapsed ? 'ChevronRight' : 'ChevronLeft')}
            size={20}
            color="var(--color-muted-foreground)"
          />
        </button>
      </div>
      {!isCollapsed && (
        <div className="px-4 py-3 border-b border-border">
          <div className="relative">
            <Icon
              name="Search"
              size={16}
              color="var(--color-muted-foreground)"
              className="absolute left-3 top-1/2 transform -translate-y-1/2"
            />
            <input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 transition-smooth"
            />
          </div>
        </div>
      )}
      <nav className="flex-1 overflow-y-auto p-4 space-y-2">
        {filteredMenu?.map((item) => (
          <div key={item?.label}>
            {item?.children ? (
              <div>
                <button
                  onClick={() => toggleSection(item?.label)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-smooth hover:bg-muted group relative ${
                    hasActiveChild(item?.children) ? 'bg-primary/5 border-l-2 border-primary' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      name={item?.icon}
                      size={20}
                      color={hasActiveChild(item?.children) ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    />
                    {!isCollapsed && (
                      <span className={`text-sm font-medium ${
                        hasActiveChild(item?.children) ? 'text-primary' : 'text-card-foreground'
                      }`}>
                        {item?.label}
                      </span>
                    )}
                  </div>
                  {!isCollapsed && (
                    <Icon
                      name={expandedSections?.[item?.label] ? 'ChevronDown' : 'ChevronRight'}
                      size={16}
                      color="var(--color-muted-foreground)"
                      className="transition-transform duration-200"
                    />
                  )}
                </button>
                {expandedSections?.[item?.label] && !isCollapsed && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-muted pl-2">
                    {item?.children?.map((child) => (
                      <button
                        key={child?.path}
                        onClick={() => handleNavigation(child?.path)}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-md transition-smooth hover:bg-muted relative group ${
                          isActive(child?.path) ? 'bg-primary text-white shadow-sm' : 'text-card-foreground'
                        }`}
                      >
                        <Icon
                          name={child?.icon}
                          size={18}
                          color={isActive(child?.path) ? 'white' : 'var(--color-muted-foreground)'}
                        />
                        <span className="text-sm font-medium">{child?.label}</span>
                        {isActive(child?.path) && (
                          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-primary rounded-r" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => handleNavigation(item?.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-smooth hover:bg-muted group relative ${
                  isActive(item?.path) ? 'bg-primary text-white shadow-sm' : 'text-card-foreground'
                }`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon
                  name={item?.icon}
                  size={20}
                  color={isActive(item?.path) ? 'white' : 'var(--color-muted-foreground)'}
                />
                {!isCollapsed && (
                  <span className="text-sm font-medium">{item?.label}</span>
                )}
                {isActive(item?.path) && (
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-white rounded-r" />
                )}
                {item?.badge && !isCollapsed && (
                  <span className="ml-auto w-2 h-2 bg-error rounded-full" />
                )}
              </button>
            )}
          </div>
        ))}
      </nav>
      {!isCollapsed && (
        <div className="p-4 border-t border-border">
          <div className="bg-muted rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Icon name="Keyboard" size={16} color="var(--color-muted-foreground)" />
              <span className="text-xs font-medium text-muted-foreground">Keyboard Shortcuts</span>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Toggle Menu</span>
                <kbd className="px-2 py-0.5 bg-background rounded border border-border">Ctrl+B</kbd>
              </div>
              <div className="flex justify-between">
                <span>Search</span>
                <kbd className="px-2 py-0.5 bg-background rounded border border-border">Ctrl+K</kbd>
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;